import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Brain } from 'lucide-react';
import { formatCurrency } from '../../utils/calculations';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
const MOCK_DATA = [{
  month: 'Jan',
  income: 500000,
  expenses: 350000,
  balance: 150000
}, {
  month: 'Feb',
  income: 500000,
  expenses: 380000,
  balance: 120000
}, {
  month: 'Mar',
  income: 500000,
  expenses: 340000,
  balance: 160000
}, {
  month: 'Apr',
  income: 520000,
  expenses: 400000,
  balance: 120000
}, {
  month: 'May',
  income: 520000,
  expenses: 360000,
  balance: 160000
}, {
  month: 'Jun',
  income: 520000,
  expenses: 350000,
  balance: 170000
}, {
  month: 'Jul',
  income: 520000,
  expenses: 480000,
  balance: 40000
} // High expense month
];
export function CashflowPredictor() {
  const currentMonth = MOCK_DATA[MOCK_DATA.length - 1];
  const isRisk = currentMonth.balance < 50000;
  return <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-293">
      <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-blue-50" data-id="element-294">
        <div className="flex items-center justify-between mb-4" data-id="element-295">
          <div className="flex items-center" data-id="element-296">
            <div className="p-2 bg-white rounded-lg shadow-sm mr-3" data-id="element-297">
              <Brain className="w-5 h-5 text-purple-600" data-id="element-298" />
            </div>
            <div data-id="element-299">
              <h3 className="font-bold text-gray-900" data-id="element-300">AI Cashflow Predictor</h3>
              <p className="text-xs text-purple-600 font-medium" data-id="element-301">
                92% Confidence Score
              </p>
            </div>
          </div>
          {isRisk ? <span className="flex items-center px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-bold" data-id="element-302">
              <AlertTriangle className="w-3 h-3 mr-1" data-id="element-303" /> High Risk
            </span> : <span className="flex items-center px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold" data-id="element-304">
              <CheckCircle className="w-3 h-3 mr-1" data-id="element-305" /> Healthy
            </span>}
        </div>

        <div className="grid grid-cols-3 gap-4 mb-4" data-id="element-306">
          <div className="bg-white/60 rounded-lg p-3" data-id="element-307">
            <p className="text-xs text-gray-500 mb-1" data-id="element-308">Predicted Income</p>
            <p className="font-bold text-gray-900" data-id="element-309">
              {formatCurrency(currentMonth.income)}
            </p>
          </div>
          <div className="bg-white/60 rounded-lg p-3" data-id="element-310">
            <p className="text-xs text-gray-500 mb-1" data-id="element-311">Predicted Expenses</p>
            <p className="font-bold text-gray-900" data-id="element-312">
              {formatCurrency(currentMonth.expenses)}
            </p>
          </div>
          <div className="bg-white/60 rounded-lg p-3" data-id="element-313">
            <p className="text-xs text-gray-500 mb-1" data-id="element-314">Projected Balance</p>
            <p className={`font-bold ${isRisk ? 'text-red-600' : 'text-green-600'}`} data-id="element-315">
              {formatCurrency(currentMonth.balance)}
            </p>
          </div>
        </div>

        {isRisk && <div className="bg-red-50 border border-red-100 rounded-lg p-3 flex items-start" data-id="element-316">
            <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 mr-2 flex-shrink-0" data-id="element-317" />
            <p className="text-xs text-red-700" data-id="element-318">
              <strong data-id="element-319">Warning:</strong> High expenses predicted for July.
              Consider adjusting your payment schedule to avoid default.
            </p>
          </div>}
      </div>

      <div className="p-6" data-id="element-320">
        <h4 className="text-sm font-bold text-gray-900 mb-4" data-id="element-321">
          6-Month Trend Analysis
        </h4>
        <div className="h-48 w-full" data-id="element-322">
          <ResponsiveContainer width="100%" height="100%" data-id="element-323">
            <AreaChart data={MOCK_DATA} data-id="element-324">
              <defs data-id="element-325">
                <linearGradient id="colorIncome" x1="0" y1="0" x2="0" y2="1" data-id="element-326">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.1} data-id="element-327" />
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0} data-id="element-328" />
                </linearGradient>
                <linearGradient id="colorExpenses" x1="0" y1="0" x2="0" y2="1" data-id="element-329">
                  <stop offset="5%" stopColor="#EF4444" stopOpacity={0.1} data-id="element-330" />
                  <stop offset="95%" stopColor="#EF4444" stopOpacity={0} data-id="element-331" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-332" />
              <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
              fontSize: 12,
              fill: '#6B7280'
            }} data-id="element-333" />
              <YAxis hide data-id="element-334" />
              <Tooltip contentStyle={{
              borderRadius: '8px',
              border: 'none',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }} formatter={(value: number) => formatCurrency(value)} data-id="element-335" />
              <Area type="monotone" dataKey="income" stroke="#10B981" strokeWidth={2} fillOpacity={1} fill="url(#colorIncome)" data-id="element-336" />
              <Area type="monotone" dataKey="expenses" stroke="#EF4444" strokeWidth={2} fillOpacity={1} fill="url(#colorExpenses)" data-id="element-337" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>;
}